import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Tag, Gift, Truck, Recycle, Search, ArrowRight, ShieldCheck } from "lucide-react";
import { publicApi } from "@/lib/api";
import { ListingCard } from "@/components/common";
import { useMe } from "@/context/MeContext";

const ACTIONS = [
  { to: "/publier?type=sale", icon: Tag, title: "Vendre", text: "Publiez en 2 minutes, paiement sécurisé.", tone: "bg-forest text-sand", id: "home-action-sell" },
  { to: "/publier?type=donation", icon: Gift, title: "Donner", text: "Offrez une seconde vie, gratuitement.", tone: "bg-sun text-navy", id: "home-action-donate" },
  { to: "/debarras", icon: Recycle, title: "Débarrasser", text: "Devis de prestataires pour vider un lieu.", tone: "bg-terra text-white", id: "home-action-clearance" },
  { to: "/livraison", icon: Truck, title: "Faire livrer", text: "Estimez une livraison par coursier.", tone: "bg-muted text-forest", id: "home-action-delivery" },
];

export default function Home() {
  const [recent, setRecent] = useState([]);
  const [q, setQ] = useState("");
  const { meta } = useMe();
  const navigate = useNavigate();

  useEffect(() => {
    publicApi.get("/listings?limit=8").then((r) => setRecent(r.data.items));
  }, []);

  return (
    <div>
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 sm:pt-16 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-6 fade-up">
          <p className="inline-block rounded-full bg-sun text-navy px-4 py-1.5 text-xs font-bold tracking-[0.15em] uppercase mb-6">La braderie en ligne · France</p>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-forest tracking-tighter leading-[0.95]">
            Chinez, vendez,<br /><em className="text-terra">donnez</em> !
          </h1>
          <p className="mt-6 text-stone-600 text-base sm:text-lg max-w-md">Vendez, donnez, débarrassez et faites livrer entre voisins, avec des adresses protégées et un paiement sécurisé.</p>
          <form onSubmit={(e) => { e.preventDefault(); navigate(`/catalogue?q=${encodeURIComponent(q)}`); }} className="mt-8 flex items-center gap-2 bg-white rounded-full border border-stone-200 shadow-sm p-1.5 max-w-lg">
            <Search className="w-5 h-5 text-stone-400 ml-3" aria-hidden="true" />
            <input data-testid="home-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Que cherchez-vous ?" className="flex-1 bg-transparent outline-none text-sm px-2" />
            <button data-testid="home-search-btn" className="rounded-full bg-forest text-sand px-5 py-2.5 text-sm font-semibold hover:bg-forest/90">Rechercher</button>
          </form>
        </div>
        <div className="lg:col-span-6 relative fade-up" style={{ animationDelay: "120ms" }}>
          <img src="/img/hero.jpg" alt="Mobilier vintage" className="rounded-2xl w-full aspect-[5/4] object-cover shadow-sm" />
          <div className="absolute -bottom-6 left-6 bg-white/90 backdrop-blur-xl rounded-2xl border border-stone-200 p-4 flex items-center gap-3 shadow-md">
            <ShieldCheck className="w-6 h-6 text-forest" aria-hidden="true" />
            <div className="text-sm"><p className="font-semibold">Adresse exacte masquée</p><p className="text-stone-500 text-xs">Révélée seulement après réservation</p></div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-20 grid grid-cols-2 lg:grid-cols-4 gap-4">
        {ACTIONS.map((a, i) => (
          <Link key={a.to} to={a.to} data-testid={a.id} className={`${a.tone} rounded-2xl p-6 sm:p-8 group hover:-translate-y-1 transition-transform duration-300 fade-up ${i === 0 ? "lg:row-span-1" : ""}`} style={{ animationDelay: `${i * 60}ms` }}>
            <a.icon className="w-7 h-7" aria-hidden="true" />
            <p className="font-serif text-3xl mt-6">{a.title}</p>
            <p className="text-sm opacity-80 mt-1">{a.text}</p>
            <ArrowRight className="w-4 h-4 mt-4 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
          </Link>
        ))}
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-20">
        <p className="eyebrow mb-4">Catégories</p>
        <div className="flex flex-wrap gap-2">
          {meta.categories.map((c) => (
            <Link key={c} to={`/catalogue?category=${encodeURIComponent(c)}`} data-testid={`home-category-${c}`} className="rounded-full border border-stone-200 bg-white px-4 py-2 text-sm hover:bg-forest hover:text-white hover:border-forest transition-colors">{c}</Link>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-20">
        <div className="flex items-end justify-between mb-8">
          <h2 className="font-serif text-3xl sm:text-4xl text-forest">Annonces récentes</h2>
          <Link to="/catalogue" data-testid="home-see-all" className="text-sm font-semibold text-forest flex items-center gap-1 hover:gap-2 transition-all">Tout voir <ArrowRight className="w-4 h-4" /></Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6" data-testid="home-recent-grid">
          {recent.map((l, i) => <ListingCard key={l.id} l={l} index={i} />)}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-24 grid md:grid-cols-2 gap-8 items-center">
        <img src="/img/market.jpg" alt="Marché aux puces" className="rounded-2xl aspect-[4/3] object-cover w-full" />
        <div>
          <h2 className="font-serif text-3xl sm:text-4xl text-forest">Une brocante de quartier, en confiance.</h2>
          <ul className="mt-6 space-y-3 text-stone-600 text-sm">
            <li>— Publication gratuite, commission affichée avant tout paiement.</li>
            <li>— Messagerie protégée : liens suspects masqués, blocage en un clic.</li>
            <li>— Dons sans contrepartie financière, réservations avec expiration.</li>
            <li>— Annonces et photos modérées, avec possibilité de recours.</li>
          </ul>
        </div>
      </section>
    </div>
  );
}
